import React, {
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from "react";
import { Transaction } from "@common/types/transaction";
import {
    BulkActionId,
    BulkUpdateMode,
    BulkUpdateStatus,
    BulkUpdateDetails,
    BulkUpdateParams,
} from "../../transactions/TransactionsBulkActions/lib";
import { useBulkActions } from "../../transactions/TransactionsBulkActions/useBulkActions";
import { SelectionControls } from "../../transactions/TransactionsBulkActions/TransactionsActionBar/SelectionControls";
import { ActionButton } from "../../transactions/TransactionsBulkActions/TransactionsActionBar/ActionButton";
import { PaginatedTransactionsActionsContext } from "../../transactions/PaginatedTransactionsTable.context";
import { NameForValueToAssign } from "../SimilarTransactionsUpdateToast/SimilarTransactionsUpdateToast";
import styles from "./SimilarTransactionsUpdateActionsBarLegacy.module.scss";

export const SimilarTransactionsUpdateActionsBarLegacy: React.FC<{
    valueToAssign: BulkUpdateParams;
    onUndo: () => void;
    displayedChangedEntityName?: string;
}> = ({ valueToAssign, onUndo, displayedChangedEntityName }) => {
    const { selectedCount, updateDetails, updateSelected } = useBulkActions();
    const { undo } = useContext(PaginatedTransactionsActionsContext);

    const isBusy = updateDetails?.status === BulkUpdateStatus.UPDATING;

    const buttonRef = useRef<HTMLButtonElement>(null);

    const newDetails: BulkUpdateDetails = useMemo(() => {
        const base = {
            status: BulkUpdateStatus.UPDATING,
            transactionsCount: selectedCount,
            mode: BulkUpdateMode.FILTERS,
            ...valueToAssign,
        };

        if (
            valueToAssign.update === "counterpartyId" &&
            displayedChangedEntityName
        ) {
            return { ...base, counterpartyName: displayedChangedEntityName };
        }

        if (
            valueToAssign.update === "coa" ||
            valueToAssign.update === "accrualCoa"
        ) {
            const accountName =
                displayedChangedEntityName ?? valueToAssign.accountName;
            return { ...base, accountName };
        }

        return base;
    }, [selectedCount, valueToAssign, displayedChangedEntityName]);

    const [transactionsToUndo, setTransactionsToUndo] = useState<Record<
        number,
        Transaction
    > | null>(null);

    const shouldShowUndoButton = useMemo(
        () => transactionsToUndo && Object.keys(transactionsToUndo).length > 0,
        [transactionsToUndo],
    );

    const handleAssign = useCallback(async () => {
        const result = await updateSelected(newDetails);
        if (result?.transactionsBeforeUpdate) {
            setTransactionsToUndo(result.transactionsBeforeUpdate);
        }
        onUndo();
    }, [updateSelected, newDetails, onUndo]);

    const handleUndo = useCallback(async () => {
        await undo({
            updateDetailsForUndo: newDetails,
            previousTransactions: transactionsToUndo!,
        });
        setTransactionsToUndo(null);
    }, [undo, newDetails, transactionsToUndo]);

    const name = (
        <NameForValueToAssign
            valueToAssign={valueToAssign}
            displayedChangedEntityName={displayedChangedEntityName}
        />
    );

    if (!selectedCount) {
        return null;
    }

    return (
        <section className={styles.actionBar}>
            {selectedCount > 0 && (
                <main className={styles.wrapper}>
                    <SelectionControls />
                    <span className={styles.separator} />
                    {shouldShowUndoButton && (
                        <ActionButton
                            action={{
                                id: BulkActionId.Undo,
                                name: "Undo",
                                ref: buttonRef,
                                tooltip: "Undo",
                            }}
                            variant="actionBar"
                            disabled={isBusy}
                            onClick={handleUndo}
                        />
                    )}
                    {!shouldShowUndoButton && (
                        <ActionButton
                            action={{
                                id: BulkActionId.Assign,
                                name: <>Assign {name}</>,
                                ref: buttonRef,
                                tooltip: "Assign",
                            }}
                            variant="actionBar"
                            disabled={isBusy}
                            onClick={handleAssign}
                        />
                    )}
                </main>
            )}
        </section>
    );
};
